import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  RadioGroup,
  Radio,
  FormControlLabel,
  Button,
  CircularProgress,
} from '@mui/material';
import { keyframes } from '@mui/system';
import { ClinicIcon, LockIcon, EyeIcon, EyeOffIcon } from '../auth/authIcons';
import { gpLogin } from '../../rtk-apis/gp/gpApi';
import { setGpSession, isValidGp } from '../../Utils/gpAuth';

// ── Theme tokens (navy + teal) — shared look with LoginV2 ───────────────────
const ekg = keyframes`
  to { stroke-dashoffset: 0; }
`;
const fieldSx = {
  mb: 1.75,
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#0b1726',
    borderRadius: '12px',
    color: '#e7eef7',
    '& fieldset': { borderColor: '#243652' },
    '&:hover fieldset': { borderColor: '#314a6b' },
    '&.Mui-focused fieldset': {
      borderColor: '#2dd4bf',
      boxShadow: '0 0 0 3px rgba(45,212,191,.15)',
    },
  },
  '& .MuiInputBase-input': { color: '#e7eef7' },
  '& input::placeholder': { color: '#6a7e9c', opacity: 1 },
  '& svg': { color: '#6a7e9c' },
};
const btnSx = {
  mt: 2,
  height: 50,
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: 15,
  fontWeight: 700,
  color: '#04221d',
  background: 'linear-gradient(90deg,#14b8a6,#2dd4bf)',
  boxShadow: '0 8px 24px -8px rgba(45,212,191,.6)',
  '&:hover': { background: 'linear-gradient(90deg,#0f9e8e,#26c2ad)' },
  '&.Mui-disabled': { color: '#04221d', opacity: 0.6 },
};
const radioSx = { color: '#6a7e9c', '&.Mui-checked': { color: '#2dd4bf' } };

/**
 * GP portal login (v2) — provider number + password. Split-screen design matching
 * LoginV2. Separate page + API from the staff login (full isolation, GP JWT).
 */
const GpLoginV2: React.FC = () => {
  const navigate = useNavigate();
  const [providerNumber, setProviderNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isValidGp()) {
      navigate('/gp/reports');
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!providerNumber.trim() || !password) {
      setError('Provider number and password are required');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const result = await gpLogin(providerNumber.trim(), password);
      setGpSession(result.token, result.user);
      navigate('/gp/reports');
    } catch (err: any) {
      setError(err?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      className="loginv2"
      sx={{
        minHeight: '100dvh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
        background: 'radial-gradient(120% 100% at 0% 0%, #0e2740 0%, #03101d 55%, #020a13 100%)',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' },
          width: '100%',
          maxWidth: 960,
          overflow: 'hidden',
          borderRadius: '22px',
          backgroundColor: '#0b1726',
          border: '1px solid #243652',
          boxShadow: '0 30px 80px -30px rgba(0,0,0,.75)',
        }}
      >
        {/* Brand panel (shared with LoginV2) */}
        <Box
          sx={{
            position: 'relative',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'flex-start',
            gap: 2,
            p: '40px 36px',
            color: '#e7eef7',
            overflow: 'hidden',
            background: 'radial-gradient(130% 90% at 0% 0%, #16314e 0%, #0a1828 58%, #06101d 100%)',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              fontSize: 20,
              fontWeight: 700,
            }}
          >
            <Box
              component="span"
              sx={{
                color: '#fb7185',
                fontSize: 22,
                filter: 'drop-shadow(0 0 10px rgba(251,113,133,.5))',
              }}
            >
              ❤
            </Box>
            <span>
              Cardiac
              <Box
                component="b"
                sx={{ color: '#2dd4bf' }}
              >
                Health
              </Box>
            </span>
          </Box>
          <Box
            component="svg"
            viewBox="0 0 600 120"
            preserveAspectRatio="none"
            aria-hidden="true"
            sx={{
              position: 'relative',
              width: '100%',
              height: 92,
              mt: 1,
              mb: 0.5,
              zIndex: 1,
              opacity: 0.95,
            }}
          >
            <polyline
              points="0,60 180,60 210,60 224,28 244,96 262,60 360,60 384,44 404,80 424,60 600,60"
              fill="none"
              stroke="rgba(45,212,191,.16)"
              strokeWidth={2}
            />
            <Box
              component="polyline"
              points="0,60 180,60 210,60 224,28 244,96 262,60 360,60 384,44 404,80 424,60 600,60"
              fill="none"
              stroke="#2dd4bf"
              strokeWidth={2.5}
              sx={{
                filter: 'drop-shadow(0 0 6px rgba(45,212,191,.6))',
                strokeDasharray: '240 1400',
                strokeDashoffset: 1640,
                animation: `${ekg} 3.4s linear infinite`,
              }}
            />
          </Box>
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: "'Spectral',serif",
                fontSize: 30,
                lineHeight: 1.16,
                fontWeight: 600,
                mb: 2.25,
              }}
            >
              Reports, delivered
              <br />
              to your inbox.
            </Typography>
            <Box
              component="ul"
              sx={{
                listStyle: 'none',
                p: 0,
                m: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
              }}
            >
              {[
                'Echo reports addressed to your provider number',
                'View the report, images and measurements',
                'Read-only and secure — no software to install',
                'Delivered straight from the reporting clinic',
              ].map(t => (
                <Box
                  component="li"
                  key={t}
                  sx={{
                    position: 'relative',
                    pl: '20px',
                    color: '#9fb2cc',
                    fontSize: 13.5,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '7px',
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      backgroundColor: '#2dd4bf',
                      boxShadow: '0 0 8px rgba(45,212,191,.7)',
                    },
                  }}
                >
                  {t}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* GP form panel */}
        <Box
          sx={{
            backgroundColor: '#0e1c2e',
            p: { xs: '32px 26px', md: '44px 40px' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: "'Spectral',serif",
                fontSize: 27,
                fontWeight: 600,
                color: '#e7eef7',
              }}
            >
              GP Portal
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#6a7e9c', mt: 0.5 }}>
              Sign in with your provider number to view reports addressed to you.
            </Typography>
          </Box>

          <RadioGroup
            row
            value="gp"
            onChange={e => {
              if (e.target.value === 'clinician') navigate('/login');
            }}
            sx={{ mb: 2.5, gap: 2 }}
          >
            <FormControlLabel
              value="clinician"
              control={
                <Radio
                  size="small"
                  sx={radioSx}
                />
              }
              label="Clinician"
              sx={{ m: 0, '& .MuiFormControlLabel-label': { color: '#9fb2cc', fontSize: 13 } }}
            />
            <FormControlLabel
              value="gp"
              control={
                <Radio
                  size="small"
                  sx={radioSx}
                />
              }
              label="Referring GP"
              sx={{ m: 0, '& .MuiFormControlLabel-label': { color: '#9fb2cc', fontSize: 13 } }}
            />
          </RadioGroup>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              type="text"
              autoComplete="username"
              placeholder="Provider number (e.g. 0410696F)"
              value={providerNumber}
              onChange={e => setProviderNumber(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ClinicIcon />
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
            <TextField
              fullWidth
              autoComplete="current-password"
              placeholder="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: '#6a7e9c' }}
                    >
                      {showPassword ? <EyeIcon /> : <EyeOffIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />

            {error && (
              <Typography sx={{ color: '#fb7185', fontSize: 13, mb: 1 }}>{error}</Typography>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disableElevation
              disabled={loading}
              sx={btnSx}
            >
              {loading ? (
                <>
                  <CircularProgress
                    size={18}
                    thickness={5}
                    sx={{ color: '#04221d', mr: 1 }}
                  />
                  Signing in…
                </>
              ) : (
                'Sign In'
              )}
            </Button>

            <Typography sx={{ fontSize: 12, color: '#6a7e9c', textAlign: 'center', mt: 2 }}>
              No account or forgotten password? Contact the clinic that sends you reports.
            </Typography>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default GpLoginV2;
